import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { CartItem, Product, PromoCode, GiftOptions } from '@/types';
import { cartService } from '@/services/cart';
import { useAuthStore } from '@/stores/auth';
import { useNotifications } from '@/composables/useNotifications';
import { useCurrencyStore } from '@/stores/currency';
import { abandonedCartsApiService } from '@/services/api/abandonedCarts';
import { getEffectiveDisplayPriceForCurrency } from '@/utils/currency';

export const useCartStore = defineStore('cart', () => {
  const GIFT_WRAP_PYG = 5000;
  const items = ref<CartItem[]>([]);
  const appliedPromoCode = ref<PromoCode | null>(null);
  const giftOptions = ref<GiftOptions>({
    isGift: false,
    giftMessage: '',
    giftWrap: false,
    recipientName: '',
    recipientEmail: '',
  });
  const isLoading = ref(false);
  const lastActivity = ref<Date>(new Date());

  const { showNotification } = useNotifications();

  const itemCount = computed(() => {
    return items.value.reduce((total, item) => total + item.quantity, 0);
  });

  const subtotal = computed(() => {
    const currencyStore = useCurrencyStore();
    const selectedCurrency = currencyStore.currentCurrency;

    return items.value.reduce((total, item) => {
      const price = getEffectiveDisplayPriceForCurrency(item.product, selectedCurrency);

      return total + (price * item.quantity);
    }, 0);
  });

  const discountAmount = computed(() => {
    if (!appliedPromoCode.value) return 0;

    const promo = appliedPromoCode.value;
    const discount = promo.type === 'percentage'
      ? subtotal.value * (promo.value / 100)
      : promo.value;

    return Math.min(discount, promo.maxDiscountAmount || discount);
  });

  const giftWrapPrice = computed(() => {
    const currencyStore = useCurrencyStore();
    const selectedCurrency = currencyStore.currentCurrency;

    if (selectedCurrency === 'USD') {
      return currencyStore.convertPrice(GIFT_WRAP_PYG, 'USD');
    }

    return GIFT_WRAP_PYG;
  });

  const giftWrapCost = computed(() => {
    if (!giftOptions.value.giftWrap) return 0;

    return giftWrapPrice.value;
  });

  const taxAmount = computed(() => 0); // Los precios ya incluyen IVA

  const shippingCost = computed(() => {
    return 0; // Envío gratis por ahora
  });

  const total = computed(() => {
    return subtotal.value + taxAmount.value + shippingCost.value + giftWrapCost.value - discountAmount.value;
  });

  const addItem = async (product: Product, quantity: number = 1, options?: Record<string, string>) => {
    try {
      const existingItemIndex = items.value.findIndex(
        item => item.productId === product.id &&
          JSON.stringify(item.selectedOptions) === JSON.stringify(options)
      );

      if (existingItemIndex >= 0) {
        items.value[existingItemIndex].quantity += quantity;
      } else {
        const newItem: CartItem = {
          id: Date.now().toString(),
          productId: product.id,
          product,
          quantity,
          selectedOptions: options,
          isGift: false,
          giftMessage: '',
        };
        items.value.push(newItem);
      }

      lastActivity.value = new Date();
      await saveCart();

      // Show enhanced notification with actions
      showNotification(
        `${product.name} añadido al carrito`,
        'success',
        5000,
        [
          {
            text: 'Ver Carrito',
            action: () => window.location.href = '/cart'
          },
          {
            text: 'Finalizar Compra',
            action: () => window.location.href = '/checkout'
          }
        ]
      );
    } catch (error) {
      showNotification('Error al añadir el producto al carrito', 'error');
    }
  };

  const updateQuantity = async (itemId: string, quantity: number) => {
    try {
      const itemIndex = items.value.findIndex(item => item.id === itemId);
      if (itemIndex >= 0) {
        if (quantity <= 0) {
          items.value.splice(itemIndex, 1);
        } else {
          items.value[itemIndex].quantity = quantity;
        }
        lastActivity.value = new Date();
        await saveCart();
      }
    } catch (error) {
      showNotification('Error al actualizar la cantidad', 'error');
    }
  };

  const updateItemGift = async (itemId: string, isGift: boolean, giftMessage?: string) => {
    try {
      const itemIndex = items.value.findIndex(item => item.id === itemId);
      if (itemIndex >= 0) {
        items.value[itemIndex].isGift = isGift;
        items.value[itemIndex].giftMessage = giftMessage || '';
        lastActivity.value = new Date();
        await saveCart();
      }
    } catch (error) {
      showNotification('Error al actualizar opciones de regalo', 'error');
    }
  };

  const updateGiftOptions = async (options: Partial<GiftOptions>) => {
    try {
      giftOptions.value = { ...giftOptions.value, ...options };
      lastActivity.value = new Date();
      await saveCart();
    } catch (error) {
      showNotification('Error al actualizar opciones de regalo', 'error');
    }
  };

  const removeItem = async (itemId: string) => {
    try {
      const itemIndex = items.value.findIndex(item => item.id === itemId);
      if (itemIndex >= 0) {
        const itemName = items.value[itemIndex].product.name;

        // Remove from local state
        items.value.splice(itemIndex, 1);
        lastActivity.value = new Date();

        // saveCart will call trackAbandonedCart which handles backend update/delete
        await saveCart();
        showNotification(`${itemName} eliminado del carrito`, 'success');
      }
    } catch (error) {
      showNotification('Error al eliminar el producto', 'error');
    }
  };

  const clearCart = async (options?: { skipRemoteDelete?: boolean; silent?: boolean; preservePendingOrder?: boolean }) => {
    try {
      try {
        const pendingOrderJson = localStorage.getItem('pending_order');
        if (pendingOrderJson) {
          const pendingOrder = JSON.parse(pendingOrderJson) as { status?: string };
          if (!options?.preservePendingOrder && (pendingOrder.status === 'RECHAZADO' || pendingOrder.status === 'CANCELADO')) {
            localStorage.removeItem('pending_order');
            localStorage.removeItem('checkout_data');
          }
        }
      } catch (storageError) {
        console.warn('⚠️ [Cart] No se pudo limpiar el pedido pendiente local', storageError);
      }

      items.value = [];
      appliedPromoCode.value = null;
      giftOptions.value = {
        isGift: false,
        giftMessage: '',
        giftWrap: false,
        recipientName: '',
        recipientEmail: '',
      };
      lastActivity.value = new Date();

      if (!options?.skipRemoteDelete) {
        // Delete abandoned cart from backend
        const sessionId = getSessionId();
        await abandonedCartsApiService.deleteCartBySession(sessionId);
      }

      // Already deleted remotely above; avoid triggering a second DELETE in trackAbandonedCart
      await saveCart({ skipRemoteTracking: true });
      if (!options?.silent) {
        showNotification('Carrito vaciado', 'success');
      }
    } catch (error) {
      showNotification('Error al vaciar el carrito', 'error');
    }
  };

  const restoreCart = async (cartSnapshot: {
    items?: CartItem[];
    appliedPromoCode?: PromoCode | null;
    giftOptions?: GiftOptions;
  }, options?: { skipRemoteTracking?: boolean }) => {
    try {
      items.value = Array.isArray(cartSnapshot.items) ? cartSnapshot.items : [];
      appliedPromoCode.value = cartSnapshot.appliedPromoCode || null;
      giftOptions.value = cartSnapshot.giftOptions || {
        isGift: false,
        giftMessage: '',
        giftWrap: false,
        recipientName: '',
        recipientEmail: '',
      };
      lastActivity.value = new Date();
      await saveCart({ skipRemoteTracking: options?.skipRemoteTracking });
    } catch (error) {
      showNotification('Error al restaurar el carrito', 'error');
      throw error;
    }
  };

  const applyPromoCode = async (code: string): Promise<boolean> => {
    try {
      isLoading.value = true;
      const promoCode = await cartService.validatePromoCode(code);

      if (promoCode.minOrderAmount && subtotal.value < promoCode.minOrderAmount) {
        showNotification(`El monto mínimo para este código es Gs. ${(promoCode.minOrderAmount * 1000).toLocaleString()}`, 'error');
        return false;
      }

      appliedPromoCode.value = promoCode;
      await saveCart();
      showNotification(`Código promocional aplicado: ${promoCode.code}`, 'success');
      return true;
    } catch (error: any) {
      showNotification(error.message || 'Código promocional inválido', 'error');
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const removePromoCode = async () => {
    try {
      appliedPromoCode.value = null;
      await saveCart();
      showNotification('Código promocional removido', 'success');
    } catch (error) {
      showNotification('Error al remover el código promocional', 'error');
    }
  };

  const saveCart = async (options?: { skipRemoteTracking?: boolean }) => {
    const cartData = {
      items: items.value,
      appliedPromoCode: appliedPromoCode.value,
      giftOptions: giftOptions.value,
      lastActivity: lastActivity.value,
    };

    // Save to localStorage
    localStorage.setItem('cart', JSON.stringify(cartData));

    // Track abandoned cart (will delete from backend if empty, or update if has items)
    if (!options?.skipRemoteTracking) {
      await trackAbandonedCart();
    }
  };

  const trackAbandonedCart = async () => {
    try {
      const authStore = useAuthStore();
      const sessionId = getSessionId();

      if (items.value.length === 0) {
        // If cart is empty, try to delete the abandoned cart from backend
        try {
          await abandonedCartsApiService.deleteCartBySession(sessionId);
        } catch (deleteError) {
          console.error('❌ [Cart] Error deleting abandoned cart:', deleteError);
        }
        return;
      }

      const currencyStore = useCurrencyStore();
      const sanitizedItems = items.value
        .map(item => ({
          ...item,
          productId: String(item.productId || item.product?.id || '').trim(),
        }))
        .filter(item => item.productId);

      if (sanitizedItems.length === 0) {
        console.warn('⚠️ [Cart] No hay items válidos para guardar como carrito abandonado');
        return;
      }

      const payload = {
        sessionId,
        userId: authStore.user?.id,
        items: sanitizedItems.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          selectedOptions: item.selectedOptions,
          price: item.product.offer_price || item.product.price,
          currency: item.product.currency_code || 'PYG'
        })),
        total: total.value,
        currency: currencyStore.currentCurrency,
        metadata: {
          userAgent: navigator.userAgent,
          referrer: document.referrer,
          currentUrl: window.location.href,
          language: navigator.language,
          exchangeRate: currencyStore.exchangeRates[currencyStore.currentCurrency]
        }
      };

      await abandonedCartsApiService.saveCart(payload);
    } catch (error) {
      console.error('Error tracking abandoned cart:', error);
    }
  };

  const getSessionId = (): string => {
    let sessionId = localStorage.getItem('session_id');
    if (!sessionId) {
      sessionId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('session_id', sessionId);
    }
    return sessionId;
  };

  const loadCart = () => {
    try {
      const cartData = localStorage.getItem('cart');
      if (cartData) {
        const parsed = JSON.parse(cartData);
        items.value = parsed.items || [];
        appliedPromoCode.value = parsed.appliedPromoCode || null;
        giftOptions.value = parsed.giftOptions || {
          isGift: false,
          giftMessage: '',
          giftWrap: false,
          recipientName: '',
          recipientEmail: '',
        };
        lastActivity.value = parsed.lastActivity ? new Date(parsed.lastActivity) : new Date();
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
    }
  };

  return {
    items: computed(() => items.value),
    appliedPromoCode: computed(() => appliedPromoCode.value),
    giftOptions: computed(() => giftOptions.value),
    isLoading: computed(() => isLoading.value),
    itemCount,
    subtotal,
    taxAmount,
    shippingCost,
    discountAmount,
    giftWrapPrice,
    giftWrapCost,
    total,
    addItem,
    updateQuantity,
    updateItemGift,
    updateGiftOptions,
    removeItem,
    clearCart,
    restoreCart,
    applyPromoCode,
    removePromoCode,
    loadCart,
  };
});
